import type { BlockThemeConfig } from 'webapp-types';
import { BlockVisualization } from 'webapp/enums/enums';

const TRAFFIC_LIGHTS_COLORS = ['#D12424', '#EE6F6F', '#E47B0B', '#FCAF5C', '#22985E', '#6DC499'];

const TRAFFIC_LIGHTS_STEPS: BlockThemeConfig.GradientStep[] = [
	{
		gradient: [0, 1],
		range: [0, 0.75],
	},
	{
		gradient: [2, 3],
		range: [0.75, 0.99],
	},
	{
		gradient: [4, 5],
		range: [0.99, 1],
	},
];

// also in functions.less (@colorTheme1colors)
const ThemeConfig: BlockThemeConfig.Type[] = [
	{
		id: 1,
		name: 'rainbow',
		colors: [
			'#00A5FF',
			'#60D1F0',
			'#59D2D7',
			'#4FD3B6',
			'#23C18F',
			'#21BB76',
			'#7BCB79',
			'#BDD875',
			'#E0DE6E',
			'#E7B597',
			'#E48FA4',
			'#E5689B',
			'#BF5C94',
			'#A15AC2',
			'#9150F6',
		],
		trafficLightsColors: TRAFFIC_LIGHTS_COLORS,
		visualizations: {
			default: {
				order: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
			},
			[BlockVisualization.PROGRESS]: {
				base: 10, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [7, 6],
					},
					{
						range: [0.5, 0.9],
						gradient: [0, 1],
					},
					{
						range: [0.9, 1],
						gradient: [3, 4],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.GAUGE]: {
				base: 10, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [7, 6],
					},
					{
						range: [0.5, 0.9],
						gradient: [0, 1],
					},
					{
						range: [0.9, 1],
						gradient: [3, 4],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.HEATMAP]: {
				steps: [
					{
						range: null,
						color: 14,
						opacity: 0.5,
					},
					{
						range: [0, 0.2],
						color: 10,
					},
					{
						range: [0.2, 0.5],
						color: 11,
					},
					{
						range: [0.5, 0.7],
						color: 12,
					},
					{
						range: [0.7, 0.9],
						color: 13,
					},
					{
						range: [0.9, 1],
						color: 14,
					},
				],
			},
			[BlockVisualization.TEXT]: {
				link: 0,
			},
		},
	},
	{
		id: 2,
		name: 'orange',
		colors: [
			'#FF5D01',
			'#FF7601',
			'#FF9401',
			'#FFB313',
			'#FFCD33',
			'#FFDA51',
			'#FDDC72',
			'#F8D292',
			'#F4BDA0',
			'#F3A39A',
			'#FE6B71',
			'#F17183',
			'#E57792',
			'#E56E98',
			'#F45797',
		],
		trafficLightsColors: TRAFFIC_LIGHTS_COLORS,
		visualizations: {
			default: {
				order: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
			},
			[BlockVisualization.PROGRESS]: {
				base: 6, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [9, 0],
					},
					{
						range: [0.5, 0.9],
						gradient: [1, 2],
					},
					{
						range: [0.9, 1],
						gradient: [3, 4],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.GAUGE]: {
				base: 7, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [9, 0],
					},
					{
						range: [0.5, 0.9],
						gradient: [1, 2],
					},
					{
						range: [0.9, 1],
						gradient: [3, 4],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.HEATMAP]: {
				steps: [
					{
						range: null,
						color: 14,
						opacity: 0.5,
					},
					{
						range: [0, 0.1],
						color: 7,
					},
					{
						range: [0.1, 0.3],
						color: 6,
					},
					{
						range: [0.3, 0.5],
						color: 4,
					},
					{
						range: [0.5, 0.7],
						color: 3,
					},
					{
						range: [0.7, 0.9],
						color: 1,
					},
					{
						range: [0.9, 1],
						color: 0,
					},
				],
			},
			[BlockVisualization.TEXT]: {
				link: 0,
			},
		},
	},
	{
		id: 3,
		name: 'purple',
		colors: [
			'#C3538B',
			'#D65D80',
			'#E07893',
			'#E994A7',
			'#F4AFBB',
			'#DFB6C3',
			'#C2BACC',
			'#A3C7E1',
			'#81B5DC',
			'#5A99CE',
			'#7190C8',
			'#8485C0',
			'#8D6EB5',
			'#8E67B1',
			'#9E54E0',
		],
		trafficLightsColors: TRAFFIC_LIGHTS_COLORS,
		visualizations: {
			default: {
				order: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
			},
			[BlockVisualization.PROGRESS]: {
				base: 3, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [0, 1],
					},
					{
						range: [0.5, 0.9],
						gradient: [6, 8],
					},
					{
						range: [0.9, 1],
						gradient: [4, 3],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.GAUGE]: {
				base: 3, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [0, 1],
					},
					{
						range: [0.5, 0.9],
						gradient: [6, 8],
					},
					{
						range: [0.9, 1],
						gradient: [4, 3],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.HEATMAP]: {
				steps: [
					{
						range: null,
						color: 5,
						opacity: 0.5,
					},
					{
						range: [0, 0.1],
						color: 5,
					},
					{
						range: [0.1, 0.3],
						color: 4,
					},
					{
						range: [0.3, 0.5],
						color: 3,
					},
					{
						range: [0.5, 0.7],
						color: 2,
					},
					{
						range: [0.7, 0.9],
						color: 1,
					},
					{
						range: [0.9, 1],
						color: 0,
					},
				],
			},
			[BlockVisualization.TEXT]: {
				link: 0,
			},
		},
	},
	{
		id: 4,
		name: 'greyscale-white',
		colors: [
			'#ffffffe6',
			'#ffffffd9',
			'#ffffffcc',
			'#ffffffbf',
			'#ffffffb3',
			'#ffffffa6',
			'#ffffff99',
			'#ffffff8c',
			'#ffffff80',
			'#ffffff73',
			'#ffffff66',
			'#ffffff59',
			'#ffffff4d',
			'#ffffff40',
			'#ffffff33',
		], // opacities 0.9 0.85 0.8 0.75 0.7 0.65 0.6 0.55 0.5 0.45 0.4 0.35 0.3 0.25 0.2
		trafficLightsColors: TRAFFIC_LIGHTS_COLORS,
		visualizations: {
			default: {
				order: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
			},
			[BlockVisualization.PROGRESS]: {
				base: 8, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [6, 7],
					},
					{
						range: [0.5, 0.9],
						gradient: [3, 4],
					},
					{
						range: [0.9, 1],
						gradient: [1, 2],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.GAUGE]: {
				base: 8, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [5, 6],
					},
					{
						range: [0.5, 0.9],
						gradient: [3, 4],
					},
					{
						range: [0.9, 1],
						gradient: [0, 1],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.HEATMAP]: {
				steps: [
					{
						range: null,
						color: 10,
					},
					{
						range: [0, 0.1],
						color: 9,
					},
					{
						range: [0.1, 0.3],
						color: 8,
					},
					{
						range: [0.3, 0.5],
						color: 6,
					},
					{
						range: [0.5, 0.7],
						color: 4,
					},
					{
						range: [0.7, 0.9],
						color: 2,
					},
					{
						range: [0.9, 1],
						color: 0,
					},
				],
			},
			[BlockVisualization.TEXT]: {
				link: 3,
			},
		},
	},
	{
		id: 5,
		name: 'greyscale-black',
		colors: [
			'#000000e6',
			'#000000d9',
			'#000000cc',
			'#000000bf',
			'#000000b3',
			'#000000a6',
			'#00000099',
			'#0000008c',
			'#00000080',
			'#00000073',
			'#00000066',
			'#00000059',
			'#0000004d',
			'#00000040',
			'#00000033',
		], // opacities 0.9 0.85 0.8 0.75 0.7 0.65 0.6 0.55 0.5 0.45 0.4 0.35 0.3 0.25 0.2
		trafficLightsColors: TRAFFIC_LIGHTS_COLORS,
		visualizations: {
			default: {
				order: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
			},
			[BlockVisualization.PROGRESS]: {
				base: 8, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [6, 7],
					},
					{
						range: [0.5, 0.9],
						gradient: [3, 4],
					},
					{
						range: [0.9, 1],
						gradient: [1, 2],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.GAUGE]: {
				base: 8, // *1
				steps: [
					{
						range: [0, 0.5],
						gradient: [5, 6],
					},
					{
						range: [0.5, 0.9],
						gradient: [3, 4],
					},
					{
						range: [0.9, 1],
						gradient: [0, 1],
					},
				],
				trafficLightsSteps: TRAFFIC_LIGHTS_STEPS,
			},
			[BlockVisualization.HEATMAP]: {
				steps: [
					{
						range: null,
						color: 10,
					},
					{
						range: [0, 0.1],
						color: 9,
					},
					{
						range: [0.1, 0.3],
						color: 8,
					},
					{
						range: [0.3, 0.5],
						color: 6,
					},
					{
						range: [0.5, 0.7],
						color: 4,
					},
					{
						range: [0.7, 0.9],
						color: 2,
					},
					{
						range: [0.9, 1],
						color: 0,
					},
				],
			},
			[BlockVisualization.TEXT]: {
				link: 3,
			},
		},
	},
];

// *1 - when changing this, must also update highcharts.less @gaugeThemeBaseColorIndexes & @progressThemeBaseColorIndexes variables

export default ThemeConfig;
